import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import ApexCharts from 'src/components/apexcharts/ApexCharts';
import { hasSkillsData } from 'src/utils/skilldar';
import { DISCLAIMER_PARAGRAPHS, DISCLAIMER_TITLE } from 'src/utils/disclaimer';
import CustomScrollbar from 'src/components/CareerDetailsPopup/CustomScrollbar';
import SkilldarMini from './SkilldarMini';
import { ArrowIcon, ChevronIcon, money } from './widgetParts';

// The career details of the wide and minimal layouts. Instead of a window over the widget, the details take the widget's place
// (with a "Back to careers" link), so they suit a short frame on a college's page, and the frame follows their height.
//
//   ‹ Careers                                                          National   West Midlands
//   Construction Plant Operator
//   ───────────────────────────────────────────────────────────────────────────────────────────
//   AVERAGE SALARY (AI)      WEEKLY HOURS (AI)      TIME TO TRAIN
//   £39,000 a year           40                     1 - 2 years
//   £750 a week
//   ───────────────────────────────────────────────────────────────────────────────────────────
//   About this career                                         │  Skilldar
//   Typical work tasks                                        │
//   ───────────────────────────────────────────────────────────────────────────────────────────
//   Colleges with supporting courses      /     Growth     /     Disclaimer
const STYLES = `
html, body, #root { background: transparent !important; }
.tsw-d { --fg:18,21,28; --ink:rgb(var(--fg)); --soft:rgba(var(--fg),.7); --faint:rgba(var(--fg),.52); --line:rgba(var(--fg),.2); --accent:#f9a748; color:var(--ink); font-family:inherit; font-size:14px; line-height:1.5; text-align:left; width:100%; box-sizing:border-box; }
.tsw-d.tone-light { --fg:255,255,255; }
.tsw-d.is-card { background:#fff; border-radius:12px; padding:20px 28px 22px; box-shadow:0 1px 2px rgba(18,21,28,.06), 0 12px 32px -12px rgba(18,21,28,.18); --fg:18,21,28; }
.tsw-d.is-plain { padding:2px; }
.tsw-d.is-fill { height:100%; overflow-y:auto; scrollbar-width:none; -ms-overflow-style:none; }
.tsw-d.is-fill::-webkit-scrollbar { display:none; width:0; }
.tsw-d-shell { position:relative; height:100%; }
.tsw-d-shell > .tsw-d.is-fill { padding-right:40px; }
.tsw-d *, .tsw-d *::before, .tsw-d *::after { box-sizing:border-box; }
.tsw-d button { font:inherit; color:inherit; }
:where(.tsw-d) :is(h2, h3, p, dl, dd, ul) { margin:0; padding:0; text-align:left; }

.tsw-d-top { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.tsw-d-back { appearance:none; background:none; border:0; padding:4px 0; display:inline-flex; align-items:center; gap:6px; font-size:13px; color:var(--soft); cursor:pointer; }
.tsw-d-back:hover { color:var(--ink); }
.tsw-d-back svg { width:14px; height:14px; transform:scaleX(-1); }
.tsw-d-tabs { display:flex; gap:18px; }
.tsw-d-tab { appearance:none; background:none; border:0; padding:2px 0; font-size:13px; color:var(--soft); cursor:pointer; border-bottom:2px solid transparent; transition:color .15s, border-color .15s; }
.tsw-d-tab:hover { color:var(--ink); }
.tsw-d-tab[aria-selected="true"] { color:var(--ink); font-weight:600; border-bottom-color:var(--accent); }
.tsw-d-title { font-size:24px; font-weight:650; line-height:1.2; letter-spacing:-.01em; text-wrap:balance; }

.tsw-d-figures { display:flex; flex-wrap:wrap; border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
.tsw-d-figure { flex:1 1 150px; padding:14px 20px 14px 0; min-width:0; }
.tsw-d-figure + .tsw-d-figure { border-left:1px solid var(--line); padding-left:20px; }
.tsw-d-figure dt { font-size:10.5px; letter-spacing:.1em; text-transform:uppercase; font-weight:600; color:var(--faint); }
.tsw-d-figure dd { font-size:22px; font-weight:650; letter-spacing:-.01em; margin-top:3px; font-variant-numeric:tabular-nums; }
.tsw-d-figure dd small { font-size:12.5px; font-weight:400; color:var(--soft); margin-left:4px; letter-spacing:0; }
.tsw-d-figure dd.is-sub { font-size:14px; font-weight:500; color:var(--soft); margin-top:0; }
.tsw-d.is-narrow .tsw-d-figure { flex:1 1 40%; padding:12px 12px 12px 0; }
.tsw-d.is-narrow .tsw-d-figure + .tsw-d-figure { border-left:0; padding-left:0; }
.tsw-d-note { font-size:12px; color:var(--faint); margin-top:8px; }

.tsw-d-cols { display:flex; gap:32px; margin-top:20px; }
.tsw-d-col-main { flex:1 1 auto; min-width:0; }
.tsw-d-col-side { flex:0 0 260px; border-left:1px solid var(--line); padding-left:28px; }
.tsw-d.is-narrow .tsw-d-cols { flex-direction:column; gap:20px; }
.tsw-d.is-narrow .tsw-d-col-side { flex:0 0 auto; border-left:0; padding-left:0; border-top:1px solid var(--line); padding-top:16px; }
.tsw-d-label { font-size:10.5px; letter-spacing:.1em; text-transform:uppercase; font-weight:600; color:var(--faint); margin-bottom:8px; }
.tsw-d-text { color:var(--ink); text-wrap:pretty; }
.tsw-d-label-gap { margin-top:20px; }
.tsw-d-tasks { list-style:none; margin-top:0; columns:2 240px; column-gap:32px; }
.tsw-d-tasks li { break-inside:avoid; padding:6px 0 6px 16px; position:relative; border-bottom:1px solid var(--line); color:var(--ink); }
.tsw-d-tasks li::before { content:''; position:absolute; left:0; top:15px; width:6px; height:1px; background:var(--soft); }
.tsw-d-empty { color:var(--soft); }

.tsw-d-section { margin-top:22px; border-top:1px solid var(--line); padding-top:16px; }
.tsw-d-section h3 { font-size:16px; font-weight:650; margin-bottom:10px; }
.tsw-d-college { margin-top:14px; }
.tsw-d-college-name { font-size:13px; font-weight:600; color:var(--soft); margin-bottom:2px; }
.tsw-d-course { border-bottom:1px solid var(--line); }
.tsw-d-course-title, .tsw-d-course-level, .tsw-d summary { text-align:left; }
.tsw-d-course > summary { list-style:none; cursor:pointer; display:grid; grid-template-columns:minmax(0,1fr) auto 16px; align-items:baseline; column-gap:16px; padding:10px 0; font-weight:600; }
.tsw-d-course > summary::-webkit-details-marker { display:none; }
.tsw-d-course > summary:hover .tsw-d-course-title { text-decoration:underline; text-decoration-color:var(--accent); text-decoration-thickness:2px; text-underline-offset:4px; }
.tsw-d-course-level { font-size:12.5px; font-weight:400; color:var(--soft); }
.tsw-d-course > summary svg { width:15px; height:15px; align-self:center; color:var(--faint); transition:transform .18s; }
.tsw-d-course[open] > summary svg { transform:rotate(90deg); }
.tsw-d-course-body { padding:0 0 14px; }
.tsw-d-course-body p { color:var(--soft); margin-bottom:8px; text-wrap:pretty; }
.tsw-d-campus { display:flex; align-items:baseline; justify-content:space-between; gap:8px 20px; flex-wrap:wrap; padding:8px 0; border-top:1px dashed var(--line); }
.tsw-d-campus-info { display:flex; flex-wrap:wrap; gap:2px 22px; color:var(--soft); font-size:13px; text-align:left; }
.tsw-d-campus-info span { text-align:left; }
.tsw-d-campus-info b { color:var(--ink); font-weight:600; margin-right:5px; }
.tsw-d-links { display:flex; gap:16px; }
.tsw-d-links a { color:var(--ink); font-weight:600; font-size:13px; text-decoration:underline; text-decoration-color:var(--accent); text-decoration-thickness:2px; text-underline-offset:4px; }
.tsw-d-links a:hover { text-decoration-color:var(--ink); }
.tsw-d-more { appearance:none; background:none; border:1px solid var(--line); border-radius:999px; padding:6px 14px; font-size:13px; color:var(--ink); cursor:pointer; margin-top:12px; transition:background .15s; }
.tsw-d-more:hover { background:rgba(var(--fg),.1); }

.tsw-d-gtabs { display:flex; gap:18px; margin-bottom:12px; border-bottom:1px solid var(--line); }
.tsw-d-gtab { appearance:none; background:none; border:0; border-bottom:2px solid transparent; margin-bottom:-1px; padding:4px 0 8px; font-size:13px; color:var(--soft); cursor:pointer; }
.tsw-d-gtab:hover { color:var(--ink); }
.tsw-d-gtab[aria-selected="true"] { color:var(--ink); font-weight:600; border-bottom-color:var(--accent); }
.tsw-d-change { display:flex; align-items:baseline; gap:12px; flex-wrap:wrap; }
.tsw-d-change strong { font-size:34px; font-weight:650; letter-spacing:-.02em; font-variant-numeric:tabular-nums; }
.tsw-d-change span { color:var(--soft); }
.tsw-d-workers { display:flex; gap:0; }
.tsw-d-workers div { flex:1 1 0; padding-right:16px; }
.tsw-d-workers div + div { border-left:1px solid var(--line); padding-left:16px; }
.tsw-d-workers dt { font-size:12.5px; color:var(--soft); }
.tsw-d-workers dd { font-size:22px; font-weight:650; font-variant-numeric:tabular-nums; }
.tsw-d-small { font-size:12.5px; color:var(--soft); margin-bottom:6px; }
.tsw-d-heading { display:flex; align-items:center; gap:8px; margin:8px 0 14px; }
.tsw-d-heading .tsw-d-title { margin:0; }
.tsw-d-info { position:relative; display:inline-flex; flex:0 0 auto; }
.tsw-d-info-btn { appearance:none; background:none; border:0; padding:3px; border-radius:50%; display:inline-flex; color:var(--soft); cursor:help; }
.tsw-d-info-btn:hover, .tsw-d-info-btn:focus-visible { color:var(--ink); }
.tsw-d-info-btn svg { width:20px; height:20px; }
.tsw-d-tip { position:absolute; left:-6px; top:calc(100% + 8px); z-index:20; width:min(440px, 86vw); padding:14px 16px; border-radius:8px; background:#12151c; color:#fff; font-size:12.5px; line-height:1.5; box-shadow:0 12px 32px -8px rgba(0,0,0,.45); opacity:0; visibility:hidden; transform:translateY(-4px); transition:opacity .15s, transform .15s, visibility .15s; pointer-events:none; }
.tsw-d-tip strong { display:block; font-size:13px; margin-bottom:6px; }
.tsw-d-tip p { margin:0 0 7px; color:rgba(255,255,255,.88); }
.tsw-d-tip p:last-child { margin-bottom:0; }
.tsw-d-info:hover .tsw-d-tip, .tsw-d-info:focus-within .tsw-d-tip { opacity:1; visibility:visible; transform:none; }
.tsw-d-state { padding:40px 0; text-align:center; color:var(--soft); }
.tsw-d-state button { appearance:none; margin-top:12px; background:none; border:1px solid var(--line); border-radius:999px; padding:7px 16px; cursor:pointer; }
.tsw-d-foot { margin-top:18px; font-size:11.5px; color:var(--faint); }

.tsw-d a:focus-visible, .tsw-d button:focus-visible, .tsw-d summary:focus-visible { outline:2px solid var(--ink); outline-offset:2px; border-radius:3px; }
@media (prefers-reduced-motion: reduce) { .tsw-d *, .tsw-d *::before, .tsw-d *::after { transition:none !important; } }
`;

const COURSES_SHOWN = 4;

const linkOf = (address) => {
  if (!address) return '';
  return String(address).toLowerCase().startsWith('http') ? address : `http://${address}`;
};
// Some descriptions hold HTML entities (&#163; for the pound sign): show them as the characters they stand for
const plain = (text) => {
  if (typeof text !== 'string' || !text.includes('&')) return text;
  const holder = document.createElement('textarea');
  holder.innerHTML = text;
  return holder.value;
};
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Courses arrive in two shapes: a list of colleges that each hold courses (campuses in "campuses"), or the courses themselves
// (campuses in "campus", the duration on the course)
const collegesOf = (courses) => {
  const list = Array.isArray(courses) ? courses : [];
  if (list.some((item) => item.collegeName)) {
    return list.map((college) => ({ name: college.collegeName, courses: (college.courses || []).map((course) => ({ ...course, campuses: course.campuses || course.campus || [] })) }));
  }
  return [{ name: '', courses: list.map((course) => ({ ...course, campuses: course.campuses || course.campus || [] })) }];
};

const CareerDetail = ({
  details,
  isLoading,
  loadError,
  onRetry,
  onBack,
  regionData,
  tabValue,
  setTabValue,
  hideColleges,
  hideGrowth,
  variant,
  tone,
  accent,
  fill,
  narrow,
  showPoweredBy,
  poweredBy,
}) => {
  const rootRef = useRef(null);
  const [growthTab, setGrowthTab] = useState('overall');
  const [showAllCourses, setShowAllCourses] = useState(false);
  const regionName = regionData?.name;
  const regionTabs = regionName ? ['National', regionName] : [];

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // the work type figures only exist for the national figures
  const workTypeShown = !regionName || tabValue === 0;
  useEffect(() => {
    if (growthTab === 'worktype' && !workTypeShown) setGrowthTab('overall');
  }, [growthTab, workTypeShown]);

  const colleges = useMemo(() => collegesOf(details?.courses), [details?.courses]);
  const allCourses = colleges.reduce((sum, college) => sum + college.courses.length, 0);

  const growth = details?.growthData;
  const qualification = growth?.QualificationGrowth;
  const barColor = accent || '#f9a748';
  const qualificationChart = useMemo(() => {
    if (!qualification?.chartData?.series) return null;
    return {
      series: JSON.parse(JSON.stringify(qualification.chartData.series)),
      options: {
        chart: { toolbar: { show: false }, background: 'transparent', foreColor: tone === 'light' && variant === 'minimal' ? 'rgba(255,255,255,.8)' : '#596173', fontFamily: 'inherit' },
        plotOptions: { bar: { borderRadius: 3, columnWidth: '46%' } },
        colors: [barColor],
        dataLabels: { enabled: false },
        grid: { borderColor: tone === 'light' && variant === 'minimal' ? 'rgba(255,255,255,.2)' : 'rgba(18,21,28,.12)', strokeDashArray: 3 },
        xaxis: { ...(qualification.chartData.xaxis || {}), convertedCatToNumeric: false },
        yaxis: { min: 0, labels: { formatter: (val) => Number(val).toFixed(2).replace(/\.00$/, '') } },
        tooltip: { theme: tone === 'light' && variant === 'minimal' ? 'dark' : 'light' },
      },
    };
  }, [qualification, tone, variant, barColor]);

  const yearly = money(details?.averageSalary?.yearly);
  const weekly = money(details?.averageSalary?.weekly);
  const overall = growth?.overallGrowth;
  const overallPercent = number(overall?.overallGrowthPercentage);
  const work = growth?.workTypeGrowth;
  const fixed = (value) => {
    const parsed = number(value);
    return parsed === null ? 'N/A' : parsed.toFixed(2);
  };

  let body;
  if (loadError) {
    body = (
      <div className="tsw-d-state" role="alert">
        <p>We couldn&apos;t load this career.</p>
        <button type="button" onClick={onRetry}>Try again</button>
      </div>
    );
  } else if (!details || isLoading) {
    body = <div className="tsw-d-state" role="status"><CircularProgress size={22} color="inherit" /></div>;
  } else {
    body = (
      <>
        <dl className="tsw-d-figures">
          <div className="tsw-d-figure">
            <dt>Average salary{details.salarySourcedFrom ? ` (${details.salarySourcedFrom})` : ''}</dt>
            <dd>{yearly || 'N/A'}{yearly && <small>a year</small>}</dd>
            {weekly && <dd className="is-sub">{weekly} a week</dd>}
          </div>
          <div className="tsw-d-figure">
            <dt>Weekly hours{details.hoursSourcedFrom ? ` (${details.hoursSourcedFrom})` : ''}</dt>
            <dd>{details.averageHours || 'N/A'}</dd>
          </div>
          <div className="tsw-d-figure">
            <dt>Time to train</dt>
            <dd>{details.transferWindow?.duration || 'N/A'}</dd>
          </div>
        </dl>
        {details.transferWindow?.description && <p className="tsw-d-note">{details.transferWindow.description}</p>}

        <div className="tsw-d-cols">
          <div className="tsw-d-col-main">
            <p className="tsw-d-label">About this career</p>
            <p className="tsw-d-text">{plain(details.description) || 'No data available.'}</p>
            {(details.tasks || []).length > 0 && (
              <>
                <p className="tsw-d-label tsw-d-label-gap">Typical work tasks</p>
                <ul className="tsw-d-tasks" aria-label="Typical work tasks">
                  {details.tasks.map((task) => <li key={task}>{plain(task)}</li>)}
                </ul>
              </>
            )}
          </div>
          <aside className="tsw-d-col-side" aria-label="Skilldar">
            <p className="tsw-d-label">Skilldar</p>
            {hasSkillsData(details.skilldarChartData) ? (
              <>
                <SkilldarMini chart={details.skilldarChartData} tone={variant === 'minimal' ? tone : 'dark'} height={narrow ? 240 : 250} />
                <p className="tsw-d-small">Hover over the chart to see each skill.</p>
              </>
            ) : (
              <p className="tsw-d-empty">No skills data for this career.</p>
            )}
          </aside>
        </div>

        {!hideColleges && (
          <section className="tsw-d-section">
            <h3>Colleges with supporting courses</h3>
            {allCourses === 0 && <p className="tsw-d-empty">No courses found.</p>}
            {(() => {
              let remaining = showAllCourses ? Infinity : COURSES_SHOWN;
              return colleges.map((college) => {
                const shown = college.courses.slice(0, Math.max(remaining, 0));
                remaining -= shown.length;
                if (!shown.length) return null;
                return (
                  <div className="tsw-d-college" key={college.name || 'courses'}>
                    {college.name && <p className="tsw-d-college-name">{college.name}</p>}
                    {shown.map((course) => (
                      <details className="tsw-d-course" key={course.id || course.title}>
                        <summary>
                          <span className="tsw-d-course-title">{course.title}</span>
                          <span className="tsw-d-course-level">{course.level ? `Level ${String(course.level).replace(/^level\s*/i, '')}` : ''}</span>
                          <ChevronIcon />
                        </summary>
                        <div className="tsw-d-course-body">
                          {course.description && <p>{plain(course.description)}</p>}
                          {course.campuses.map((campus) => (
                            <div className="tsw-d-campus" key={campus.id || campus.name || campus.pageURL}>
                              <span className="tsw-d-campus-info">
                                <span><b>Duration</b>{course.duration || '2 Years - Full Time'}</span>
                                {campus.name && <span><b>Campus</b>{campus.name}</span>}
                              </span>
                              <span className="tsw-d-links">
                                {campus.enquiryURL && <a href={linkOf(campus.enquiryURL)} target="_blank" rel="noreferrer">Enquire</a>}
                                {campus.applyURL && <a href={linkOf(campus.applyURL)} target="_blank" rel="noreferrer">Apply</a>}
                                {campus.pageURL && <a href={linkOf(campus.pageURL)} target="_blank" rel="noreferrer">More info</a>}
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                );
              });
            })()}
            {allCourses > COURSES_SHOWN && (
              <button type="button" className="tsw-d-more" onClick={() => setShowAllCourses(!showAllCourses)}>
                {showAllCourses ? 'Show fewer courses' : `Show all ${allCourses} courses`}
              </button>
            )}
          </section>
        )}

        {!hideGrowth && (
          <section className="tsw-d-section">
            <h3>Growth</h3>
            <p className="tsw-d-small">Predicted demand by {growth?.regionalGrowth?.year || 'the year shown'}</p>
            <div className="tsw-d-gtabs" role="tablist">
              {[['overall', 'Overall change'], ['qualification', 'By qualification'], ...(workTypeShown ? [['worktype', 'Work type']] : [])].map(([key, label]) => (
                <button type="button" role="tab" key={key} className="tsw-d-gtab" aria-selected={growthTab === key} onClick={() => setGrowthTab(key)}>{label}</button>
              ))}
            </div>
            {growthTab === 'overall' && (
              <div className="tsw-d-change">
                <strong>{overallPercent === null ? 'N/A' : `${overallPercent > 0 ? '+' : ''}${overallPercent.toFixed(2)} %`}</strong>
                {overall?.duration && <span>overall change {overall.duration}</span>}
              </div>
            )}
            {growthTab === 'qualification' && (
              <>
                <p className="tsw-d-small">Growth by qualification level {qualification?.year}</p>
                {qualificationChart ? <ApexCharts width="100%" type="bar" height={280} chartData={qualificationChart} /> : <p className="tsw-d-empty">No data available.</p>}
              </>
            )}
            {growthTab === 'worktype' && (
              <>
                <p className="tsw-d-small">Predicted workforce in {work?.year}</p>
                <dl className="tsw-d-workers">
                  <div><dt>Full-time</dt><dd>{fixed(work?.fullTime)}</dd></div>
                  <div><dt>Part-time</dt><dd>{fixed(work?.partTime)}</dd></div>
                  <div><dt>Self-employed</dt><dd>{fixed(work?.selfEmployeed)}</dd></div>
                </dl>
              </>
            )}
          </section>
        )}

      </>
    );
  }

  const panel = (
    <div
      ref={rootRef}
      className={`tsw-d ${variant === 'wide' ? 'is-card' : 'is-plain'} tone-${tone === 'light' ? 'light' : 'dark'}${fill ? ' is-fill' : ''}${narrow ? ' is-narrow' : ''}`}
      style={accent ? { '--accent': accent } : undefined}
    >
      <style>{STYLES}</style>
      <div className="tsw-d-top">
        <button type="button" className="tsw-d-back" onClick={onBack}>
          <ArrowIcon />
          Back to careers
        </button>
        {regionTabs.length > 0 && !hideGrowth && (
          <div className="tsw-d-tabs" role="tablist">
            {regionTabs.map((label, index) => (
              <button type="button" role="tab" key={label} className="tsw-d-tab" aria-selected={tabValue === index} onClick={() => setTabValue(index)}>{label}</button>
            ))}
          </div>
        )}
      </div>
      <div className="tsw-d-heading">
        <h2 className="tsw-d-title">{details?.title || ' '}</h2>
        {details && (
          <span className="tsw-d-info">
            <button type="button" className="tsw-d-info-btn" aria-label={DISCLAIMER_TITLE} aria-describedby="tsw-d-disclaimer">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 11v5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="7.9" r="1.1" fill="currentColor" />
              </svg>
            </button>
            <span className="tsw-d-tip" role="tooltip" id="tsw-d-disclaimer">
              <strong>{DISCLAIMER_TITLE}</strong>
              {DISCLAIMER_PARAGRAPHS.map((paragraph) => <p key={paragraph.slice(0, 24)}>{paragraph}</p>)}
            </span>
          </span>
        )}
      </div>
      {body}
      {showPoweredBy && <p className="tsw-d-foot">{poweredBy}</p>}
    </div>
  );
  if (!fill) return panel;
  // in a fixed-height frame the details scroll inside it, with the widget's own scrollbar beside them
  return (
    <div className="tsw-d-shell">
      {panel}
      <CustomScrollbar targetRef={rootRef} placement="absolute" scheme={variant === 'minimal' && tone === 'light' ? 'onDark' : 'onLight'} />
    </div>
  );
};

CareerDetail.propTypes = {
  details: PropTypes.object,
  isLoading: PropTypes.bool,
  loadError: PropTypes.bool,
  onRetry: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  regionData: PropTypes.object,
  tabValue: PropTypes.number,
  setTabValue: PropTypes.func.isRequired,
  hideColleges: PropTypes.bool,
  hideGrowth: PropTypes.bool,
  variant: PropTypes.string,
  tone: PropTypes.string,
  accent: PropTypes.string,
  fill: PropTypes.bool,
  narrow: PropTypes.bool,
  showPoweredBy: PropTypes.bool,
  poweredBy: PropTypes.string,
};

export default CareerDetail;
